import React, { memo, useCallback } from 'react';
import Box from '@mui/material/Box';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import FormField from './FormField';
import SelectInput from './SelectInput';
import ToggleChip from './ToggleChip';
import { LABEL_FORMATS, LabelFormat, Option } from './labelForm';
import { cardSx } from './styles';

interface LabelSettingsCardProps {
  partners: readonly Option[];
  partnerId: string;
  onPartnerChange: (partnerId: string) => void;
  labelFormats: readonly LabelFormat[];
  onToggleFormat: (format: LabelFormat) => void;
  generateLabels: boolean;
  onGenerateLabelsChange: (generateLabels: boolean) => void;
}

/** Partner, output formats and rate-shop toggle. */
const LabelSettingsCard: React.FC<LabelSettingsCardProps> = ({
  partners,
  partnerId,
  onPartnerChange,
  labelFormats,
  onToggleFormat,
  generateLabels,
  onGenerateLabelsChange,
}) => {
  const toggleRateOnly = useCallback(() => onGenerateLabelsChange(!generateLabels), [generateLabels, onGenerateLabelsChange]);
  // Drop the Thermal/Image/Print hints on narrow screens so the three formats stay on one line.
  const theme = useTheme();
  const compactFormats = useMediaQuery(theme.breakpoints.down('md'));
  return (
    <Box
      component="section"
      aria-label="Label settings"
      sx={{
        ...(cardSx as object),
        display: 'grid',
        // Formats column sizes to its content so the chips never wrap; Partner takes the rest.
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) auto' },
        gap: '20px 32px',
      }}
    >
      <FormField label="Partner">
        <SelectInput
          label="Partner"
          value={partnerId}
          options={partners}
          onChange={onPartnerChange}
          placeholder={partners.length ? 'Select a partner' : 'No partners available'}
          disabled={!partners.length}
        />
      </FormField>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Typography id="label-formats-heading" component="span" sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
          Label formats
        </Typography>
        <Box role="group" aria-labelledby="label-formats-heading" sx={{ display: 'flex', gap: 1, flexWrap: 'nowrap', '& > *': { flex: { xs: '1 1 0', lg: '0 0 auto' }, minWidth: 0 } }}
        >
          {LABEL_FORMATS.map((f) => (
            <ToggleChip
              key={f.value}
              size="large"
              label={f.label}
              description={compactFormats ? undefined : f.description}
              selected={labelFormats.includes(f.value)}
              onClick={() => onToggleFormat(f.value)}
            />
          ))}
        </Box>
      </Box>

      <Box sx={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 1.5, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Switch
          checked={!generateLabels}
          onChange={toggleRateOnly}
          inputProps={{ 'aria-labelledby': 'rate-shop-label', 'aria-describedby': 'rate-shop-help' }}
        />
        <Box>
          <Typography id="rate-shop-label" sx={{ fontWeight: 500, fontSize: 14 }}>
            Rate shop only
          </Typography>
          <Typography id="rate-shop-help" sx={{ fontSize: 12, color: 'text.secondary' }}>
            Return carrier rates without creating labels
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default memo(LabelSettingsCard);
